import type { ReactNode } from 'react'
import { useState } from 'react'
import { Modal, ModalConfirmation, ModalInfo } from '../../components/molecules'
import type { ModalOptionsType } from '../../components/molecules/Modal'
import { getRandomUUID } from '../../utils'
import { useAnal } from '..'
import { ModalContext } from './ModalContext'
import type { ContentOpenModalPropsType, ModalInstance } from './types'

export const ModalProvider = (props: { children: ReactNode }) => {
  const [modals, setModals] = useState<ModalInstance[]>([])
  const { logEvent } = useAnal()

  const openModal = <GResult,>(
    content: (props: ContentOpenModalPropsType<GResult>) => ReactNode,
    options?: ModalOptionsType & {
      initialValue?: GResult
    },
  ) => {
    const id = getRandomUUID()
    const name = options?.name ?? 'unnamed_modal'

    const closeModal = new Promise<GResult>((resolve) => {
      logEvent('modal_opened', {
        name,
      })

      setModals((currentModals) => [
        ...currentModals,
        {
          id,
          name,
          content,
          resolve,
          result: options?.initialValue,
          options,
        },
      ])
    })
    return {
      id,
      closeModal,
    }
  }

  const openConfirmationModal = (
    props: Omit<Parameters<typeof ModalConfirmation>[0], 'onClose'>,
    options?: ModalOptionsType,
  ) => {
    return openModal<'main' | 'secondary'>(
      ({ close }) => (
        <ModalConfirmation
          {...props}
          onClose={(result) => close(result)}
        />
      ),
      {
        ...options,
        showCloseButton: options?.showCloseButton ?? false,
        closeOnClickOutside: options?.closeOnClickOutside ?? false,
      },
    )
  }

  const openInfoModal = (
    props: Omit<Parameters<typeof ModalInfo>[0], 'onClose'>,
    options?: ModalOptionsType,
  ) => {
    return openModal<undefined>(
      ({ close }) => (
        <ModalInfo
          {...props}
          onClose={close}
        />
      ),
      {
        ...options,
        showCloseButton: options?.showCloseButton ?? false,
        closeOnClickOutside: options?.closeOnClickOutside ?? false,
      },
    )
  }

  const closeModal = (
    params: (
      | {
          id: string
        }
      | {
          name: string
        }
    ) & {
      result?: unknown
    },
  ) => {
    setModals((currentModals) => {
      const modalToCloseIndex = currentModals.findIndex((modal) =>
        'id' in params ? modal.id === params.id : modal.name === params.name,
      )
      if (modalToCloseIndex !== -1) {
        const modalsDuplicate = [...currentModals]
        const [closedModal] = modalsDuplicate.splice(modalToCloseIndex, 1)
        closedModal.resolve(params.result)
        logEvent('modal_closed', {
          name: closedModal.name ?? 'unnamed_modal',
          result: params.result ?? 'no_result',
        })
        return modalsDuplicate
      }
      return currentModals
    })
  }

  const setModalResult = (id: string, value: unknown) => {
    setModals((currentModals) =>
      currentModals.map((modal) =>
        modal.id === id ? { ...modal, result: value } : modal,
      ),
    )
  }

  return (
    <ModalContext.Provider
      value={{
        openModal,
        openInfoModal,
        openConfirmationModal,
        closeModal,
        modalsCount: modals.length,
      }}
    >
      {props.children}
      {modals.map((modal) => (
        <Modal
          key={modal.id}
          {...modal.options}
          onClose={(result) =>
            closeModal({
              id: modal.id,
              result: result,
            })
          }
        >
          {modal.content({
            close: (value) =>
              closeModal({
                id: modal.id,
                result: value,
              }),
            result: modal.result,
            setResult: (value) => setModalResult(modal.id, value),
          })}
        </Modal>
      ))}
    </ModalContext.Provider>
  )
}
